import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, ExternalLink, GripVertical, Play, Plus } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { GenieModals, GenieActionModal } from "@/components/agent/core";
import { GenieAvatar } from "@/components/GenieAvatar";
import { GenieChannelIndicators } from "@/components/agent/GenieChannelIndicators";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { Agent } from "@/types/agent/agent";
import { AgentGroup } from "@/types/agent/agent-group";
import { GenieCategories } from "@/types/shared/categories";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { useUpdateAgent } from "@/hooks/agent";
import { useIsMobile } from "@/hooks/ui/useIsMobile";
import { GroupSelector } from "./GroupSelector";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";


interface GenieTableViewProps {
  agents: Agent[];
  groups: AgentGroup[];
  isDragEnabled?: boolean;
  isReordering?: boolean;
  /** Trailing empty row that starts a new genie setup. */
  showUnlockRow?: boolean;
}

const formatRelativeDate = (dateStr: string | null): string => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

/* ------------------------------------------------------------------ */
/*  Sortable row group – one <tbody> per agent so transforms work     */
/* ------------------------------------------------------------------ */

interface SortableAgentRowsProps {
  agent: Agent;
  groups: AgentGroup[];
  hasGroups: boolean;
  totalColumns: number;
  isExpanded: boolean;
  categoryDropdownAgentId: string | null;
  categoryDropdownRef: React.RefObject<HTMLDivElement | null>;
  updateAgentPending: boolean;
  isDragEnabled: boolean;
  userId?: string;
  onRowClick: (agentId: string) => void;
  onCategoryDropdownToggle: (agentId: string) => void;
  onCategoryUpdate: (agent: Agent, categoryId: string) => void;
  onAction: (action: GenieActionModal, agent: Agent) => void;
  onShare: (agent: Agent) => void;
}

const SortableAgentRows = ({
  agent,
  groups,
  hasGroups,
  totalColumns,
  isExpanded,
  categoryDropdownAgentId,
  categoryDropdownRef,
  updateAgentPending,
  isDragEnabled,
  userId,
  onRowClick,
  onCategoryDropdownToggle,
  onCategoryUpdate,
  onAction,
  onShare,
}: SortableAgentRowsProps) => {
  const navigate = useNavigate();
  const isTeamGenie = userId && agent.user_id !== userId;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: agent.id,
    disabled: !isDragEnabled,
  });

  const category = agent.category_data
    ? agent.category_data
    : GenieCategories.find((c) => c.id === agent.category);
  const group = groups.find((g) => g.id === agent.group_id);
  const brand = agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const brandGradient =
    agent.branding?.gradientEnabled && agent.branding?.secondaryColor
      ? `linear-gradient(${agent.branding.gradientAngle || 135}deg, ${brand}, ${agent.branding.secondaryColor})`
      : "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)";

  const openLive = () => {
    const urlName = agent.url_name ?? agent.page?.url_name;
    if (!urlName) return toast.error("Unable to open live page");
    navigate(`/live-agent/${urlName}`);
  };

  // Same distinct destinations as the grid card — three side sheets + one page.
  const expansionActions = [
    { label: "Share", genieIcon: "share" as const, onClick: () => onShare(agent) },
    { label: "Configure", genieIcon: "settings" as const, onClick: () => onAction("prompt", agent) },
    { label: "Insights", genieIcon: "target" as const, onClick: () => onAction("insightsDrawer", agent) },
    { label: "Full details", genieIcon: "eye" as const, onClick: () => navigate(`/genie-hub/my-genies/${agent.id}`) },
  ];

  return (
    <tbody ref={setNodeRef} style={style}>
      {/* Data row */}
      <TableRow
        className={cn(
          "cursor-pointer transition-colors",
          isExpanded && "bg-blue-50/30"
        )}
        onClick={() => onRowClick(agent.id)}
      >
        <TableCell className="py-2 px-2">
          <div className="flex items-center gap-1">
            <button
              {...attributes}
              {...listeners}
              className={cn(
                "p-0.5 rounded transition-colors flex-shrink-0",
                isDragEnabled
                  ? "cursor-grab active:cursor-grabbing hover:bg-gray-100"
                  : "cursor-default opacity-30"
              )}
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
            >
              <GripVertical className="w-3.5 h-3.5 text-gray-400" />
            </button>
            {isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            )}
            <div className="relative w-8 h-8 flex-shrink-0">
              <GenieAvatar
                size={32}
                color={brand}
                customAvatarUrl={resolveGenieAvatarUrl(agent)}
                secondaryColor={agent.branding?.secondaryColor}
                gradientEnabled={agent.branding?.gradientEnabled}
                hasCustomBranding={Boolean(agent.branding?.primaryColor && agent.branding.primaryColor !== DEFAULT_BRANDING.primaryColor)}
              />
              {/* Mobile status — the Status column is hidden at phone width. */}
              <span
                aria-label={agent.is_active ? "Active" : "Inactive"}
                title={agent.is_active ? "Active" : "Inactive"}
                className={cn(
                  "md:hidden absolute -bottom-0.5 -right-0.5 z-10 w-2.5 h-2.5 rounded-full ring-2 ring-white",
                  agent.is_active ? "bg-emerald-500" : "bg-gray-300",
                )}
              />
            </div>
          </div>
        </TableCell>
        <TableCell className="py-2">
          <div className="min-w-0">
            <span className="font-medium text-gray-900 truncate block">{agent.name}</span>
            {((agent.welcome_message ?? agent.page?.welcome_message) || agent.description) && (
              <p className="text-xs text-gray-400 truncate mt-0.5">
                {(agent.welcome_message ?? agent.page?.welcome_message) || agent.description}
              </p>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500 md:hidden">
              {category && <span>{category.label}</span>}
              {(hasGroups || agent.group) && <span>{group?.name || "No group"}</span>}
              <span>{formatRelativeDate(agent.created_at)}</span>
              <GenieChannelIndicators genie={agent} brand={brand} compact showKnowledge />
            </div>
          </div>
        </TableCell>
        <TableCell className="hidden py-2 md:table-cell">
          <div
            className="relative"
            ref={categoryDropdownAgentId === agent.id ? categoryDropdownRef : undefined}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCategoryDropdownToggle(agent.id);
              }}
              disabled={updateAgentPending}
              className="transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              {category ? (
                <Badge
                  className="text-xs font-medium cursor-pointer"
                  style={{
                    backgroundColor: `${category.color}15`,
                    color: category.color,
                    border: `1px solid ${category.color}30`,
                  }}
                >
                  {category.label}
                  <ChevronDown className="w-3 h-3 ml-1 inline-block" />
                </Badge>
              ) : (
                <span className="text-gray-400 text-sm hover:text-gray-600">
                  Set category
                  <ChevronDown className="w-3 h-3 ml-1 inline-block" />
                </span>
              )}
            </button>

            {categoryDropdownAgentId === agent.id && (
              <div
                className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-20"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">
                    Change Category
                  </div>
                  {GenieCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCategoryUpdate(agent, cat.id);
                      }}
                      disabled={updateAgentPending}
                      className="w-full flex items-center justify-between px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors disabled:opacity-50"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span>{cat.label}</span>
                      </div>
                      {agent.category === cat.id && (
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TableCell>
        <TableCell className="hidden py-2 md:table-cell">
          <span className={`text-xs font-medium ${isTeamGenie ? "text-gray-700" : "text-gray-600"}`}>
            {isTeamGenie ? "Team" : "Personal"}
          </span>
        </TableCell>
        {(hasGroups || agent.group) && (
          <TableCell className="hidden py-2 md:table-cell" onClick={(e) => e.stopPropagation()}>
            <GroupSelector
              agentId={agent.id}
              currentGroupId={agent.group_id}
              groups={groups}
              currentGroup={agent.group}
              agent={agent}
              brandColor={brand}
            />
          </TableCell>
        )}
        <TableCell className="hidden py-2 md:table-cell">
          <div className="flex items-center gap-1.5">
            <div
              className={`w-2 h-2 rounded-full ${agent.is_active ? "bg-emerald-500" : "bg-gray-300"}`}
            />
            <span className={`text-xs ${agent.is_active ? "text-emerald-600" : "text-gray-400"}`}>
              {agent.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        </TableCell>
        <TableCell className="hidden py-2 px-2 md:table-cell">
          <GenieChannelIndicators genie={agent} brand={brand} compact showKnowledge className="flex-nowrap" />
        </TableCell>
        <TableCell className="hidden py-2 text-sm text-gray-500 md:table-cell">
          {formatRelativeDate(agent.created_at)}
        </TableCell>
        <TableCell className="py-2 px-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAction("advanced", agent);
            }}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            title="Open configuration"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </TableCell>
      </TableRow>

      {/* Expansion row */}
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={totalColumns} className="!p-0 !border-0">
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{ maxHeight: isExpanded ? "240px" : "0px" }}
          >
            {isExpanded && (
              <div className="px-4 py-4 bg-gray-50/60 flex flex-wrap items-center gap-2.5">
                <button
                  onClick={openLive}
                  className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl text-white font-semibold shadow-md hover:opacity-90 transition-all"
                  style={{ background: brandGradient }}
                >
                  <Play className="w-4 h-4 fill-white" />
                  Test your Genie
                </button>
                {expansionActions.map((action) => (
                  <button
                    key={action.label}
                    onClick={action.onClick}
                    className="inline-flex items-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition-opacity hover:opacity-80"
                    style={{ backgroundColor: `${brand}12`, color: brand }}
                  >
                    <GenieIcon
                      name={action.genieIcon}
                      size={18}
                      color={brand}
                      secondaryColor={agent.branding?.secondaryColor || undefined}
                      gradientEnabled={!!agent.branding?.gradientEnabled}
                      gradientAngle={agent.branding?.gradientAngle || 135}
                      label={action.label}
                    />
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
    </tbody>
  );
};

/* ------------------------------------------------------------------ */
/*  Main table component                                              */
/* ------------------------------------------------------------------ */

const GenieTableView = ({ agents, groups, isDragEnabled = false, isReordering = false, showUnlockRow = false }: GenieTableViewProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasGroups = groups.length > 0;
  const isMobile = useIsMobile();
  // Full-width rows must span only the visible columns — spanning hidden ones
  // makes the browser add empty columns that squeeze Name on mobile.
  const totalColumns = isMobile ? 3 : hasGroups ? 9 : 8;

  const [expandedAgentId, setExpandedAgentId] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<GenieActionModal | null>(null);
  const [categoryDropdownAgentId, setCategoryDropdownAgentId] = useState<string | null>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  const updateAgent = useUpdateAgent();
  const expandedAgent = agents.find((a) => a.id === expandedAgentId) || null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setCategoryDropdownAgentId(null);
      }
    };
    if (categoryDropdownAgentId) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [categoryDropdownAgentId]);

  const handleCategoryUpdate = async (agent: Agent, categoryId: string) => {
    try {
      await updateAgent.mutateAsync({ agent, updateData: { category: categoryId } });
    } catch (error) {
      console.error("Failed to update category:", error);
    } finally {
      setCategoryDropdownAgentId(null);
    }
  };

  const handleRowClick = (agentId: string) => {
    if (expandedAgentId === agentId) {
      setExpandedAgentId(null);
      setActiveModal(null);
    } else {
      setExpandedAgentId(agentId);
      setActiveModal(null);
    }
  };

  const handleShare = (agent: Agent) => {
    if (!(agent.url_name ?? agent.page?.url_name)) {
      toast.error("Unable to generate share link");
      return;
    }
    setExpandedAgentId(agent.id);
    setActiveModal("shareDrawer");
  };

  const handleAction = (action: GenieActionModal, agent: Agent) => {
    setExpandedAgentId(agent.id);
    setActiveModal(action);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <Table className="table-fixed w-full">
        <TableHeader>
          <TableRow className="bg-gray-50/80">
            <TableHead className="w-[80px]" />
            <TableHead className="md:w-[25%]">Name</TableHead>
            <TableHead className="hidden w-[11%] md:table-cell">Category</TableHead>
            <TableHead className="hidden w-[9%] md:table-cell">Ownership</TableHead>
            {hasGroups && <TableHead className="hidden w-[13%] md:table-cell">Group</TableHead>}
            <TableHead className="hidden w-[9%] md:table-cell">Status</TableHead>
            <TableHead className="hidden w-[14%] md:table-cell"></TableHead>
            <TableHead className="hidden w-[8%] md:table-cell">Created</TableHead>
            <TableHead className="w-[52px]" />
          </TableRow>
        </TableHeader>
        {agents.map((agent) => (
          <SortableAgentRows
            key={agent.id}
            agent={agent}
            groups={groups}
            hasGroups={hasGroups}
            totalColumns={totalColumns}
            isExpanded={expandedAgentId === agent.id}
            categoryDropdownAgentId={categoryDropdownAgentId}
            categoryDropdownRef={categoryDropdownRef}
            updateAgentPending={updateAgent.isPending}
            isDragEnabled={isDragEnabled}
            userId={user?.id}
            onRowClick={handleRowClick}
            onCategoryDropdownToggle={(id) =>
              setCategoryDropdownAgentId(categoryDropdownAgentId === id ? null : id)
            }
            onCategoryUpdate={handleCategoryUpdate}
            onAction={handleAction}
            onShare={handleShare}
          />
        ))}
        {showUnlockRow && (
          <tbody>
            <TableRow
              className="group cursor-pointer hover:bg-blue-50/30"
              onClick={() => navigate("/user-hub/genie-setup")}
            >
              <TableCell colSpan={totalColumns} className="py-2 px-2">
                <button type="button" className="flex items-center gap-3 w-full text-left">
                  <span className="ml-[38px] w-8 h-8 flex-shrink-0 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 transition-colors group-hover:border-genie-primary group-hover:text-genie-primary">
                    <Plus className="w-4 h-4" />
                  </span>
                  <span className="font-medium text-gray-400 transition-colors group-hover:text-gray-700">
                    Unlock your next genie
                  </span>
                </button>
              </TableCell>
            </TableRow>
          </tbody>
        )}
      </Table>

      {expandedAgent && (
        <GenieModals
          genie={expandedAgent}
          activeModal={activeModal}
          onClose={() => setActiveModal(null)}
          onSwitchModal={setActiveModal}
        />
      )}
    </div>
  );
};

export default GenieTableView;
